import { Agent, Avatar, CreatedAt, Timestamped } from "../traits";
import { HealthCheckSummary } from "./health";
import { PlaybookArtifact } from "./playbooks";
import { Property } from "./topology";

export interface ConfigChange extends CreatedAt {
  id: string;
  config_id: string;
  external_change_id: string;
  deleted_at?: string; // deleted at timestamp of the config (not the change)
  change_type: string;
  severity?: string;
  source: string;
  summary: string;
  patches?: string;
  diff?: string;
  details: string;
  external_created_by: string;
  config?: ConfigItem;
  config_class?: string;
  type?: string;
  name?: string;
  created_by?: string;
  tags?: Record<string, any>;
  first_observed?: string;
  count?: number;
  inserted_at?: string;
  artifacts?: PlaybookArtifact[];
}

export interface Change {
  change_type: string;
  total: number;
  severity?: string;
}

export interface Costs {
  cost_per_minute?: number;
  cost_total_1d?: number;
  cost_total_30d?: number;
}

export function isCostsEmpty(costs: Costs) {
  return (
    !costs.cost_per_minute && !costs.cost_total_1d && !costs.cost_total_30d
  );
}

export interface ConfigItem extends Timestamped, Avatar, Agent, Costs {
  id: string;
  name: string;
  deleted_at?: string;
  status?: string;
  external_id?: string[];
  config_class?: string;
  type: string;
  path?: string;
  changes?: number;
  analysis?: Record<string, any>;
  tags?: Record<string, any>;
  labels?: Record<string, string>;
  allTags?: Record<string, any>;
  config?: Record<string, any>;
  description?: string | null;
  health?: "healthy" | "unhealthy" | "warning" | "unknown";
  related_ids?: string[];
  agent?: {
    id: string;
    name: string;
  };
  scraper?: {
    id: string;
    name: string;
  };
  summary?: {
    relationships?: number;
    analysis?: number;
    changes?: number;
    playbook_runs?: number;
    checks?: number;
  };
  properties?: {
    icon: string;
    name: string;
    links: {
      label: string;
      url: string;
    }[];
  }[];
  last_scraped_time?: string;
}

export interface ConfigItemGraphData extends ConfigItem {
  expanded?: boolean;
  expandable?: boolean;
  items?: Pick<ConfigItem, "type">[];
  isIntermediaryNode?: boolean;
}

export interface ConfigAccessSummary {
  config_id?: string;
  config_name?: string | null;
  config_type?: string | null;
  external_user_id: string | null;
  user: string;
  email: string;
  role?: string | null;
  user_type?: string | null;
  external_group_id: string | null;
  last_signed_in_at?: string | null;
  last_reviewed_at?: string | null;
  created_at: string;
}

export interface ConfigAccessSummaryByUser {
  external_user_id: string;
  user: string;
  email: string;
  access_count: number;
  distinct_roles: number;
  distinct_configs: number;
  last_signed_in_at?: string | null;
  latest_grant?: string | null;
  config_types?: string[] | null;
}

export interface ConfigAccessSummaryByGroup {
  external_group_id: string;
  group: string;
  access_count: number;
  distinct_users: number;
  distinct_roles: number;
  distinct_configs: number;
  last_signed_in_at?: string | null;
  latest_grant?: string | null;
  config_types?: string[] | null;
}

export interface ExternalUser {
  id: string;
  name?: string | null;
  email?: string | null;
  aliases?: string[] | null;
  user_type?: string | null;
}

export interface ConfigAccessSummaryByConfig {
  config_id: string;
  config_name: string;
  config_type: string;
  access_count: number;
  distinct_users: number;
  distinct_roles: number;
  last_signed_in_at?: string | null;
  latest_grant?: string | null;
}

export type ConfigAccessGroupBy = "user" | "group" | "config";

export interface ConfigAccessLog {
  config_id: string;
  external_user_id: string;
  scraper_id: string;
  created_at: string;
  mfa?: boolean | null;
  properties?: Record<string, any> | null;
  external_users?: {
    name: string;
    user_email?: string | null;
  } | null;
}

export interface ConfigTypeRelationships extends Timestamped {
  config_id: string;
  related_id: string;
  property: string;
  selector_id: string;
  configs: ConfigItem;
  related: ConfigItem;
}

export interface Analysis {
  category: string;
  severity: string;
  description: string;
  analysis_type: string;
  analyzer: string;
}

export interface ConfigAnalysis extends Analysis, CreatedAt, Avatar {
  id: string;
  config_id: string;
  summary: string;
  status: string;
  message: string;
  properties?: Property[] | null;
  analysis?: Record<string, any> | string | null;
  sanitizedMessageHTML?: string;
  sanitizedMessageTxt?: string;
  first_observed: string;
  last_observed: string;
  source?: string | null;
  config?: ConfigItem;
}

export type ConfigSummary = {
  type: string;
  analysis?: Record<string, any>;
  changes?: string;
  count: number;
  health: {
    healthy: number;
    unknown: number;
    [key: string]: number;
  };
  cost_per_minute?: number;
  cost_total_1d?: number;
  cost_total_30d?: number;
  agent?: {
    id: string;
    name: string;
  };
};

export type ConfigHealthCheckView = HealthCheckSummary & {
  config_id: string;
};
